import { Directive, EventEmitter, Output } from "@angular/core";

import { UserVerificationService } from "@box/common/auth/abstractions/user-verification/user-verification.service.abstraction";
import { TwoFactorProviderType } from "@box/common/auth/enums/two-factor-provider-type";
import { VerificationType } from "@box/common/auth/enums/verification-type";
import { SecretVerificationRequest } from "@box/common/auth/models/request/secret-verification.request";
import { TwoFactorProviderRequest } from "@box/common/auth/models/request/two-factor-provider.request";
import { TwoFactorService } from "@box/common/auth/two-factor";
import { AuthResponseBase } from "@box/common/auth/types/auth-response";
import { I18nService } from "@box/common/platform/abstractions/i18n.service";
import { LogService } from "@box/common/platform/abstractions/log.service";
import { PlatformUtilsService } from "@box/common/platform/abstractions/platform-utils.service";
import { DialogService, ToastService } from "@box/components";

/**
 * Base class for two-factor setup components (ex: email, yubikey, webauthn, duo).
 */
@Directive({})
export abstract class TwoFactorSetupMethodBaseComponent {
  // FIXME(https://box.atlassian.net/browse/CL-903): Migrate to Signals
  // eslint-disable-next-line @angular-eslint/prefer-output-emitter-ref
  @Output() onUpdated = new EventEmitter<boolean>();

  type: TwoFactorProviderType | undefined;
  organizationId: string | null = null;
  twoFactorProviderType = TwoFactorProviderType;
  enabled = false;
  authed = false;

  protected secret: string | undefined;
  protected verificationType: VerificationType | undefined;
  protected componentName = "";

  constructor(
    protected twoFactorService: TwoFactorService,
    protected i18nService: I18nService,
    protected platformUtilsService: PlatformUtilsService,
    protected logService: LogService,
    protected userVerificationService: UserVerificationService,
    protected dialogService: DialogService,
    protected toastService: ToastService,
  ) {}

  protected auth(authResponse: AuthResponseBase) {
    this.secret = authResponse.secret;
    this.verificationType = authResponse.verificationType;
    this.authed = true;
  }

  protected async disableMethod() {
    const confirmed = await this.dialogService.openSimpleDialog({
      title: { key: "disable" },
      content: { key: "twoStepDisableDesc" },
      type: "warning",
    });

    if (!confirmed) {
      return;
    }

    const request = await this.buildRequestModel(TwoFactorProviderRequest);
    if (this.type === undefined) {
      throw new Error("Two-factor provider type is required");
    }
    request.type = this.type;
    if (this.organizationId != null) {
      await this.twoFactorService.putTwoFactorOrganizationDisable(this.organizationId, request);
    } else {
      await this.twoFactorService.putTwoFactorDisable(request);
    }
    this.enabled = false;
    this.toastService.showToast({
      variant: "success",
      title: "",
      message: this.i18nService.t("twoStepDisabled"),
    });
    this.onUpdated.emit(false);
  }

  protected async buildRequestModel<T extends SecretVerificationRequest>(
    requestClass: new () => T,
  ) {
    if (this.secret === undefined || this.verificationType === undefined) {
      throw new Error("User verification data is missing");
    }
    return this.userVerificationService.buildRequest(
      {
        secret: this.secret,
        type: this.verificationType,
      },
      requestClass,
      true,
    );
  }
}
